import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  UserCheck,
  Package, 
  ShoppingCart, 
  HeadphonesIcon,
  Bell, 
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  Shield,
  ClipboardList,
  FileText,
  MessageCircle,
  Boxes,
  ArrowLeftRight,
  DollarSign,
  Warehouse,
  Box,
  UsersRound,
  HeartPulse,
  Apple,
  UtensilsCrossed,
  Leaf,
  Coffee
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { NutriGymLogo } from './NutriGymLogo';
import { Button } from './ui/button';

const Sidebar = () => {
  const { user, logout, isAdmin, isDistributor } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({ users: true });

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getNavItems = () => {
    const baseItems = [
      { icon: LayoutDashboard, label: t('dashboard'), path: `/${user?.user_type}` },
    ];

    if (isAdmin) {
      return [
        ...baseItems,
        { icon: Bell, label: t('notifications'), path: '/admin/notifications' },
        { type: 'group', id: 'health', icon: HeartPulse, label: 'Herramientas de Salud', children: [
          { icon: ClipboardList, label: 'Test de Salud', path: '/admin/health-tests' },
          { icon: FileText, label: 'Recetas Médicas', path: '/admin/prescriptions' },
          { icon: Apple, label: 'Mi Dieta', path: '/admin/diets', children: [
            { icon: UtensilsCrossed, label: 'Platos', path: '/admin/diets/platos' },
            { icon: Coffee, label: 'Bebidas', path: '/admin/diets/bebidas' },
            { icon: Leaf, label: 'Ingredientes', path: '/admin/diets/ingredientes' },
          ]},
        ]},
        { type: 'group', id: 'operations', icon: Boxes, label: 'Operaciones', children: [
          { icon: Boxes, label: 'Productos', path: '/admin/operations/products' },
          { icon: ShoppingCart, label: 'Órdenes Op.', path: '/admin/operations/orders' },
          { icon: ArrowLeftRight, label: 'Movimientos', path: '/admin/operations/movements' },
          { icon: Box, label: 'Prod. Virtuales', path: '/admin/operations/virtual-products' },
        ]},
        { type: 'group', id: 'finance', icon: DollarSign, label: 'Finanzas', children: [
          { icon: DollarSign, label: 'Ganancias', path: '/admin/finance/ganancias' },
          { icon: Warehouse, label: 'Capital Inventario', path: '/admin/finance/capital' },
        ]},
        { type: 'group', id: 'users', icon: UsersRound, label: 'Gestión de Usuarios', children: [
          { icon: Shield, label: 'Administradores', path: '/admin/admins' },
          { icon: Users, label: t('distributors'), path: '/admin/distributors' },
          { icon: UserCheck, label: t('clients'), path: '/admin/clients' },
        ]},
        { type: 'group', id: 'support', icon: HeadphonesIcon, label: 'Soporte Técnico', children: [
          { icon: HeadphonesIcon, label: t('supportClients'), path: '/admin/support-clients' },
          { icon: HeadphonesIcon, label: t('supportDistributors'), path: '/admin/support-distributors' },
          { icon: MessageCircle, label: 'Mensajes', path: '/admin/messages' },
        ]},
      ];
    }

    if (isDistributor) {
      return [
        ...baseItems,
        { icon: UserCheck, label: t('clients'), path: '/distributor/clients' },
        { icon: Package, label: t('supplements'), path: '/distributor/supplements' },
        { icon: ShoppingCart, label: t('orders'), path: '/distributor/orders' },
        { icon: HeadphonesIcon, label: 'Soporte', path: '/distributor/support' },
        { icon: Bell, label: t('notifications'), path: '/distributor/notifications' },
      ];
    }

    // Client
    return [
      { icon: ClipboardList, label: 'Test de Salud', path: '/client/health-tests' },
      { icon: LayoutDashboard, label: t('dashboard'), path: '/client/dashboard' },
      { icon: Package, label: t('supplements'), path: '/client/supplements' },
      { icon: ShoppingCart, label: t('orders'), path: '/client/orders' },
      { icon: HeadphonesIcon, label: 'Soporte', path: '/client/support' },
      { icon: Bell, label: t('notifications'), path: '/client/notifications' },
    ];
  };

  const navItems = getNavItems();

  const NavContent = () => (
    <>
      {/* Logo */}
      <div className={`p-4 ${isCollapsed ? 'px-2' : 'px-6'}`}>
        <NutriGymLogo 
          size={isCollapsed ? 'sm' : 'md'} 
          variant="dark" 
          showText={!isCollapsed}
        />
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        {navItems.map((item, idx) => {
          if (item.type === 'separator') {
            if (!item.label) return <div key={`sep-${idx}`} className="my-1" />;
            return (
              <div key={`sep-${idx}`} className={`pt-4 pb-1 ${isCollapsed ? 'px-1' : 'px-4'}`}>
                {!isCollapsed && <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">{item.label}</p>}
                {isCollapsed && <div className="border-t border-gray-200" />}
              </div>
            );
          }
          if (item.type === 'group') {
            const isExpanded = expandedGroups[item.id];
            const isChildActive = item.children.some(c => location.pathname === c.path || (c.children && c.children.some(sc => location.pathname === sc.path)));
            return (
              <div key={item.id}>
                <button
                  onClick={() => toggleGroup(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    isChildActive && !isExpanded
                      ? 'bg-[#467322]/10 text-[#467322] font-semibold'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  } ${isCollapsed ? 'justify-center px-2' : ''}`}
                  data-testid={`nav-group-${item.id}`}
                >
                  <item.icon size={20} />
                  {!isCollapsed && (
                    <>
                      <span className="flex-1 text-left">{item.label}</span>
                      <ChevronDown size={14} className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                    </>
                  )}
                </button>
                {isExpanded && !isCollapsed && (
                  <div className="ml-4 mt-0.5 space-y-0.5 border-l border-gray-200 pl-2">
                    {item.children.map(child => {
                      if (child.children) {
                        const isSubExpanded = expandedGroups[child.path];
                        const isSubChildActive = child.children.some(sc => location.pathname === sc.path);
                        return (
                          <div key={child.path}>
                            <button
                              onClick={() => toggleGroup(child.path)}
                              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-sm ${
                                isSubChildActive && !isSubExpanded ? 'bg-[#467322]/10 text-[#467322] font-semibold' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                              }`}
                              data-testid={`nav-subgroup-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <child.icon size={16} />
                              <span className="flex-1 text-left">{child.label}</span>
                              <ChevronDown size={12} className={`transition-transform duration-200 ${isSubExpanded ? 'rotate-180' : ''}`} />
                            </button>
                            {isSubExpanded && (
                              <div className="ml-3 mt-0.5 space-y-0.5 border-l border-gray-200 pl-2">
                                {child.children.map(sc => (
                                  <NavLink
                                    key={sc.path}
                                    to={sc.path}
                                    onClick={() => setIsMobileOpen(false)}
                                    className={({ isActive }) => `
                                      flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-200 text-xs
                                      ${isActive ? 'bg-[#467322]/10 text-[#467322] font-semibold shadow-sm' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'}
                                    `}
                                    data-testid={`nav-${sc.label.toLowerCase().replace(/\s+/g, '-')}`}
                                  >
                                    <sc.icon size={14} />
                                    <span>{sc.label}</span>
                                  </NavLink>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      }
                      return (
                        <NavLink
                          key={child.path}
                          to={child.path}
                          onClick={() => setIsMobileOpen(false)}
                          className={({ isActive }) => `
                            flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-sm
                            ${isActive ? 'bg-[#467322]/10 text-[#467322] font-semibold shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}
                          `}
                          data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <child.icon size={16} />
                          <span>{child.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === `/${user?.user_type}`}
              onClick={() => setIsMobileOpen(false)}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
                ${isActive 
                  ? 'bg-[#467322]/10 text-[#467322] font-semibold shadow-sm' 
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }
                ${isCollapsed ? 'justify-center px-2' : ''}
              `}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon size={20} />
              {!isCollapsed && <span>{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* User Info & Logout */}
      <div className={`p-4 border-t border-gray-200 ${isCollapsed ? 'px-2' : ''}`}>
        {!isCollapsed && (
          <div className="mb-3 px-2">
            <p className="text-gray-900 font-semibold truncate">{user?.name}</p>
            <p className="text-gray-500 text-sm truncate">{user?.email}</p>
            <span className="inline-block mt-1 px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600 capitalize">
              {user?.user_type}
            </span>
          </div>
        )}
        <Button
          variant="ghost"
          onClick={handleLogout}
          className={`w-full justify-start text-gray-600 hover:bg-gray-100 hover:text-gray-900 ${isCollapsed ? 'px-2 justify-center' : ''}`}
          data-testid="logout-button"
        >
          <LogOut size={20} />
          {!isCollapsed && <span className="ml-2">{t('logout')}</span>}
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white text-[#467322] border border-gray-200 rounded-lg shadow-sm"
        data-testid="mobile-menu-toggle"
      >
        {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed lg:static inset-y-0 left-0 z-40
          bg-white border-r border-gray-200 flex flex-col
          transition-all duration-300 ease-in-out
          ${isCollapsed ? 'w-16' : 'w-64'}
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        data-testid="sidebar"
      >
        {/* Collapse Toggle (Desktop) */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 bg-white border border-gray-200 rounded-full items-center justify-center shadow-sm hover:bg-gray-50 transition-colors"
          data-testid="collapse-sidebar"
        >
          <ChevronRight 
            size={14} 
            className={`text-gray-500 transition-transform ${isCollapsed ? '' : 'rotate-180'}`} 
          />
        </button>

        <NavContent />
      </aside>
    </>
  );
};

export default Sidebar;
